import { useEffect, useState } from 'react';
import { Avatar } from './Avatar';
import { v4 as uuid } from 'uuid';

interface Props {
  onSubmit: (name: string) => void;
}

const NAME_REGEX = /^[\p{L}\p{N}_\-. ]+$/u;
const MAX_LEN = 32;

const SUGGESTIONS = ['Alex', 'Sam', 'Jordan', 'Riley', 'Taylor', 'Casey'];

export function NamePrompt({ onSubmit }: Props) {
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  // Stable preview userId so the avatar doesn't flicker between paints.
  const [previewId] = useState(() => uuid());

  useEffect(() => {
    setErr(null);
  }, [name]);

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length < 1 || trimmed.length > MAX_LEN) {
      setErr(`Name must be 1-${MAX_LEN} characters`);
      return;
    }
    if (!NAME_REGEX.test(trimmed)) {
      setErr('Use letters, numbers, spaces, _ . - only');
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-5">
          <div className="relative">
            <Avatar userId={previewId} name={name || 'You'} size={72} ring />
            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 ring-2 ring-white" />
          </div>
        </div>

        <h2 className="text-center text-2xl font-bold text-gray-900 tracking-tight">
          Welcome to <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">VIP Chat</span>
        </h2>
        <p className="text-center text-sm text-gray-500 mt-1.5 mb-6">
          Pick a display name to join the conversation.
        </p>

        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Display name</label>
        <div className="relative">
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value.slice(0, MAX_LEN))}
            onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            placeholder="e.g. Alex"
            maxLength={MAX_LEN}
            className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 pr-12 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 tabular-nums">
            {name.length}/{MAX_LEN}
          </span>
        </div>

        {err ? (
          <div className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {err}
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-1 flex-wrap">
            <span className="text-[11px] text-gray-400">Try:</span>
            {SUGGESTIONS.map(s => (
              <button
                type="button"
                key={s}
                onClick={() => setName(s)}
                className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={name.trim().length === 0}
          className="mt-5 w-full rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-sm font-semibold py-2.5 shadow-sm hover:from-blue-700 hover:to-indigo-700 transition disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed disabled:shadow-none"
        >
          Join chat
        </button>

        <p className="mt-4 text-center text-[11px] text-gray-400">
          By continuing you agree to be respectful in chat.
        </p>
      </div>
    </div>
  );
}
